import { FormInstance } from "antd";
import { SorterResult } from "antd/es/table/interface";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import { PermissionDataInjection } from "features/permissionDataManagement/di/PermissionDataInjection";
import { PermissionDataActionEntity } from "features/permissionDataManagement/domain/entities/PermissionDataActionEntity";
import { PermissionDataDetailEntity } from "features/permissionDataManagement/domain/entities/PermissionDataDetailEntity";
import { PermissionDataRequestEntity } from "features/permissionDataManagement/domain/entities/PermissionDataRequestEntity";
import { RefObject } from "react";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { strings } from "utils/localizedStrings";
import { IPermissionDataObservedState } from "../interfaces/IPermissionData";

type PermissionDataAction = "create" | "update" | "delete";

type PermissionDataActionSuccess = {
  action: PermissionDataAction;
  needRefreshStartup: boolean;
};

class PermissionDataInput implements Disposable {
  public fetchList = new BehaviorSubject<PermissionDataRequestEntity>({});
  public fetchRole = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchDetail = new BehaviorSubject<PermissionDataRequestEntity>({});
  public fetchParentMenu = new BehaviorSubject<PermissionDataRequestEntity>({});
  public fetchUsers = new BehaviorSubject<BaseUserRequestModel>({});
  public fetchCapability = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchActions = new BehaviorSubject<{
    action?: PermissionDataAction;
    value?: PermissionDataActionEntity | PermissionDataRequestEntity;
    detail?: PermissionDataDetailEntity;
    currentRoleCode?: string;
  }>({});

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchRole.complete();
    this.fetchDetail.complete();
    this.fetchParentMenu.complete();
    this.fetchUsers.complete();
    this.fetchCapability.complete();
    this.fetchActions.complete();
  }
}

class PermissionDataOutput implements Disposable {
  public onError = new BehaviorSubject<string>("");
  public actionSuccess =
    new BehaviorSubject<PermissionDataActionSuccess | null>(null);
  public onGetDetailSuccess = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.onError.complete();
    this.actionSuccess.complete();
    this.onGetDetailSuccess.complete();
  }
}

@injectable()
export class PermissionDataViewModel extends BasedObservedViewModel<
  PermissionDataInput,
  PermissionDataOutput,
  IPermissionDataObservedState
> {
  private permissionDataUseCase = PermissionDataInjection.getUseCase();

  constructor() {
    super(new PermissionDataInput(), new PermissionDataOutput());
  }

  protected override createObservedState(): IPermissionDataObservedState {
    return {
      listLoading: false,
      formLoading: false,
      detailLoading: false,
      userLoading: false,
      list: [],
      detail: new PermissionDataDetailEntity(),
      paging: { page: 0, limit: 10, total: 0 },
      capabilities: [],
      roleList: [],
      parentMenu: [],
      userList: [],
      userPaging: { page: 0, limit: 40, total: 0 },
    };
  }

  public buildTableQuery(
    pagination: any,
    sorter: SorterResult,
    filterForm: RefObject<FormInstance<any>>,
    prevParams: PermissionDataRequestEntity,
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    const { created, ...rest } = formValues;

    const order =
      sorter?.order === "descend"
        ? `${String(sorter?.column?.dataIndex)} DESC`
        : sorter?.order === "ascend"
          ? `${String(sorter?.column?.dataIndex)} ASC`
          : "createdAt DESC";

    return {
      query: {
        ...prevParams,
        ...rest,
        created: created ?? prevParams?.created,
        page: pagination?.current - 1,
        limit: pagination?.pageSize,
        order,
      } as PermissionDataRequestEntity,
    };
  }

  private getErrorMessage(error: any): string {
    return (
      error?.error?.message ||
      error?.message ||
      strings.GlobalMessage.Error
    );
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.setObservedState({ listLoading: true, list: [] });
        const res = await this.permissionDataUseCase.getList(params);
        if (res?.data) {
          this.setObservedState({
            list: res.data,
            paging: res.pagination,
          });
        }
      } catch (error) {
        console.error(error);
        this.setObservedState({
          list: [],
          paging: { page: 0, limit: 10, total: 0 },
        });
        this.output.onError.next(this.getErrorMessage(error));
      } finally {
        this.setObservedState({ listLoading: false });
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        this.setObservedState({ detailLoading: true });
        const res = await this.permissionDataUseCase.getDetail(params);
        if (res?.data) {
          const mappedDetail =
            (new PermissionDataDetailEntity().removeUnknowValue?.(
              res.data,
            ) as PermissionDataDetailEntity) ||
            new PermissionDataDetailEntity();
          this.setObservedState({ detail: mappedDetail });
          this.output.onGetDetailSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(this.getErrorMessage(error));
      } finally {
        this.setObservedState({ detailLoading: false });
      }
    });

    this.skipFirstInitial(this.input.fetchRole, async (params) => {
      try {
        const res = await this.permissionDataUseCase.getRole(params);
        if (res?.data) {
          this.setObservedState({ roleList: res.data });
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(this.getErrorMessage(error));
      }
    });

    this.skipFirstInitial(this.input.fetchParentMenu, async (params) => {
      try {
        const res = await this.permissionDataUseCase.getParentMenu(params);
        if (res?.data) {
          this.setObservedState({ parentMenu: res.data });
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(this.getErrorMessage(error));
      }
    });

    this.skipFirstInitial(this.input.fetchCapability, async (params) => {
      try {
        const res = await this.permissionDataUseCase.getCapability(params);
        if (res?.data) {
          this.setObservedState({ capabilities: res.data });
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(this.getErrorMessage(error));
      }
    });

    this.skipFirstInitial(this.input.fetchUsers, async (params) => {
      try {
        this.setObservedState({ userLoading: true });
        const res = await this.permissionDataUseCase.getUsers(params);
        if (res) {
          const currentData =
            params?.page && params.page > 0
              ? this.observedState.userList
              : [];
          this.setObservedState({
            userList: [...currentData, ...(res.data ?? [])],
            userPaging: res.pagination,
          });
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(this.getErrorMessage(error));
      } finally {
        this.setObservedState({ userLoading: false });
      }
    });

    this.skipFirstInitial(this.input.fetchActions, async (payload) => {
      try {
        this.setObservedState({ formLoading: true });

        if (payload.action === "create" && payload.value) {
          const res = await this.permissionDataUseCase.createMenu(
            payload.value as PermissionDataActionEntity,
            payload.detail || {},
          );
          if (res?.data) {
            this.output.actionSuccess.next({
              action: "create",
              needRefreshStartup: true,
            });
          }
        }

        if (payload.action === "update" && payload.value && payload.detail) {
          const needRefreshStartup =
            this.permissionDataUseCase.shouldRefreshStartupOnUpdate(
              payload.value as PermissionDataActionEntity,
              payload.detail,
              payload.currentRoleCode,
            );
          const res = await this.permissionDataUseCase.updateMenu(
            payload.value as PermissionDataActionEntity,
            payload.detail,
          );
          if (res?.data) {
            this.output.actionSuccess.next({
              action: "update",
              needRefreshStartup,
            });
          }
        }

        if (payload.action === "delete" && payload.value) {
          const res = await this.permissionDataUseCase.deleteMenu(
            payload.value as PermissionDataRequestEntity,
          );
          if (res?.data) {
            this.output.actionSuccess.next({
              action: "delete",
              needRefreshStartup: true,
            });
          }
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(this.getErrorMessage(error));
      } finally {
        this.setObservedState({ formLoading: false });
      }
    });
  }
}
